import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTranslation from '@hooks/useTranslation';
import { KemuServiceProduct, PriceInfo, SubscriptionInterval, KemuProductFeatureValue } from '@kemu-io/kemu-types';
import {
  selectCheckoutTiers,
  selectSelectedTier,
  selectSelectedPrice,
  selectTierChangeDirection,
  setSelectedTierAction,
  setSelectedPriceAction,
  updateTierChangeDirectionAction,
  openCheckoutDrawerAction,
} from '@src/app/reducers/checkout/checkoutSlice';
import StyledButton from '@components/form-control/styledButton/styledButton';
import styles from './PricingDisplay.module.css';
import KemuSwitch from '@components/form-control/kemuSwitch/KemuSwitch';
import useAuthenticatedUser from '@hooks/useAuthenticatedUser';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import { getTierColor } from '@components/helpers';

const getTierIcon = (tierLevel: number) => {
  switch (tierLevel) {
    case 1: return '🎁'; // Free
    case 2: return '🚀'; // Starter
    case 3: return '🫂'; // Teams  
    case 4: return '🏢'; // Business
    default: return '⭐';// Enterprise
  }
};

const getTierIconColor = (tierLevel: number) => {
  switch (tierLevel) {
    case 1: return '#f59e0b'; // Plus - amber
    case 2: return '#3b82f6'; // Pro - blue
    case 3: return '#8b5cf6'; // Enterprise - purple
    default: return '#6b7280';
  }
};

const getCurrencySymbol = (currency: string) => {
  switch (currency.toUpperCase()) {
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'JPY': return '¥';
    case 'AUD': return '$';
    case 'CAD': return 'C$';
    default: return currency.toUpperCase() + ' ';
  }
};

const visibleTierLevels: number[] = [
  1, // Free
  2, // Starter
  3, // Teams
  // 4, // Business
  5, // Enterprise
];

const visibleFeatures: KemuProductFeatureValue[] = [ 
  'seats',
  'private_recipes',
  'recipe_history',
  'private_snippets',
  'private_widgets',
  // 'agentic_executions',
  'assisted_code_generation',
  // 'file_storage_size_mb',
  // 'file_storage_retention_hours',
  'custom_services',
];

const tierFeatureExclusions: Record<number, KemuProductFeatureValue[]> = {
  // Example: exclude specific features from specific tier levels
  1: ['seats'],
  2: ['seats'],
};



const findPriceForInterval = (tier: KemuServiceProduct | undefined, i: SubscriptionInterval): PriceInfo | undefined => {
  if (!tier) { return undefined; }
  return (tier.prices || []).find((p) => p.interval === i && !p.inactive);
};

const calculateDiscountPercentage = (tier: KemuServiceProduct) => {
  const monthlyPrice = findPriceForInterval(tier, 'month');
  const yearlyPrice = findPriceForInterval(tier, 'year');
  
  if (!monthlyPrice || !yearlyPrice || !monthlyPrice.unitAmountCents || !yearlyPrice.unitAmountCents) { return 0; }
  
  const monthlyTotal = monthlyPrice.unitAmountCents * 12;
  const yearlyTotal = yearlyPrice.unitAmountCents;
  const discount = ((monthlyTotal - yearlyTotal) / monthlyTotal) * 100;
  
  return Math.floor(discount);
};

/**
 * Displays available tiers with price selection and advances to payment on selection.
 * @returns React.JSX.Element
 */
const PricingDisplay = () => {
  const t = useTranslation('Checkout.Pricing');
  const dispatch = useDispatch();
  const tiers = useSelector(selectCheckoutTiers);
  const user = useAuthenticatedUser();
  const userSubscription = user.organization?.stripeSubscription;
  const selectedTier = useSelector(selectSelectedTier);
  const selectedPrice = useSelector(selectSelectedPrice);
  const tierChangeDirection = useSelector(selectTierChangeDirection);
  const [interval, setInterval] = useState<SubscriptionInterval>('month');

  const subscribedTier = useMemo(() => {
    if (!userSubscription) { return undefined; }
    const mainTier = userSubscription.items.find((product) => product.isTier);
    return tiers.find((tier) => tier.productId === mainTier?.productId);
  }, [tiers, userSubscription]);

  const currentTierLevel = useMemo(() => {
    return subscribedTier?.tierLevel || 0;
  }, [subscribedTier]);

  const orderedTiers = useMemo(() => {
    const filteredTiers = [...tiers].sort((a, b) => (a.tierLevel || 0) - (b.tierLevel || 0))
      .filter((tier) => tier.isTier && visibleTierLevels.includes(tier.tierLevel || 0))
      .map((tier) => ({
        ...tier,
        prices: tier.prices.filter((price) => !price.inactive),
      }));

    return filteredTiers;
  }, [tiers]);



  const handleTierChange = (tier: KemuServiceProduct) => {
    dispatch(setSelectedTierAction(tier));
    const price = findPriceForInterval(tier, interval);
    if (price) {
      dispatch(setSelectedPriceAction(price));
    }
    // Update tier change direction
    dispatch(updateTierChangeDirectionAction({
      currentTierLevel,
      selectedTierLevel: tier.tierLevel
    }));
  };

  useEffect(() => {
    // Keep selected price in sync with interval
    if (selectedTier) {
      const price = findPriceForInterval(selectedTier, interval);
      if (price && price.priceId !== selectedPrice?.priceId) {
        dispatch(setSelectedPriceAction(price));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, selectedTier]);

  const handleContinue = () => {
    if (selectedTier && selectedPrice) {
      dispatch(openCheckoutDrawerAction());
    }
  };

  const handleTalkToSales = () => {
    // Redirect to the contact us page.
    window.open('https://kemu.io/contact', '_blank');
  };
  
  const renderFeatures = (tier: KemuServiceProduct) => {
    // Sort tier futures alphabetically
    // const featureOrder = Object.keys(tier.features).sort((a, b) => {
    //   return a.localeCompare(b);
    // });

    const tierLevel = tier.tierLevel || 0;
    const excludedFeatures = tierFeatureExclusions[tierLevel] || [];
    const featuresToDisplay = visibleFeatures.filter((feature) => !excludedFeatures.includes(feature));

    return (
      <div className={styles.FeaturesSection}>
        {featuresToDisplay.map((key) => {
          const value = tier.features?.[key as KemuProductFeatureValue];
          const display = value === -1 ? t.withBaseKey('CommonWords')('Unlimited') : value;
          const displayValue = display === 0 ? t.withBaseKey('CommonWords')('No') 
            // : display === 1 ? t.withBaseKey('CommonWords')('Yes') 
            : display === -2 ? t.withBaseKey('CommonWords')('Customizable') 
            : display;

          return (
            <div key={key} className={styles.FeatureItem}>
              {display !== 0 ? (
                <span className={styles.FeatureIcon}>✓</span>
              ) : (
                <span className={`${styles.FeatureIcon} ${styles.Red}`}>✗</span>
              )}
              <span>
                <Tooltip title={t(`Features.${key}.Description`)} trigger={['click']} color={'#4e3effe6'}>
                  <span className={styles.FeatureName}>{t(`Features.${key}`)}</span>
                </Tooltip>
                : <b>{displayValue}</b>
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const isFreePlan = (tier: KemuServiceProduct) => {
    const price = findPriceForInterval(tier, interval);
    return price && price.unitAmountCents === 0;
  };

  const isEnterprisePlan = (tier: KemuServiceProduct) => {
    return tier.tierLevel === 5; // Enterprise tier level
  };

  useEffect(() => {
    if (subscribedTier && !selectedTier) {
      handleTierChange(subscribedTier);
    }
  }, [subscribedTier, selectedTier]);

  useEffect(() => {
    // Update tier change direction when current tier level or selected tier changes
    if (selectedTier) {
      dispatch(updateTierChangeDirectionAction({
        currentTierLevel,
        selectedTierLevel: selectedTier.tierLevel
      }));
    }
  }, [currentTierLevel, selectedTier, dispatch]);

  return (
    <div className={styles.Container}>
      <div className={styles.Header}>
        <h3 className={styles.Title}>{t('Title')}</h3>
      </div>

      <div className={styles.IntervalToggle}>
        <span className={interval === 'month' ? styles.IntervalLabelActive : styles.IntervalLabel}>
          {t('Toggle.Month')}
        </span>
        <KemuSwitch 
          checked={interval === 'year'} 
          onChange={(checked) => setInterval(checked ? 'year' : 'month')} 
          size={'default'}
        />
        <span className={interval === 'year' ? styles.IntervalLabelActive : styles.IntervalLabel}>
          {t('Toggle.Year')}
          <span className={`${styles.DiscountBadge} ${interval !== 'year' && styles.NotActive}`}>
            -{calculateDiscountPercentage(tiers[1])}%
          </span>
        </span>
      </div>

      <div className={styles.TierCardsGrid}>
        {orderedTiers.map((tier) => {
          const currentPrice = findPriceForInterval(tier, interval);
          const isSelected = selectedTier?.productId === tier.productId;
          const isCurrentPlan = subscribedTier?.productId === tier.productId;
          
          return (
            <div
              key={tier.productId}
              className={classNames(styles.TierCard, {
                [styles.TierCardSelected]: isSelected,
                [styles.TierCardGold]: tier.tierLevel === 5,
                [styles.CurrentTier]: isCurrentPlan,
              })}
              style={{ '--tier-card-bar-color': getTierColor(tier.tierLevel || 1) } as React.CSSProperties}
              onClick={() => handleTierChange(tier)}
            >
              {/* {isCurrentPlan && (
                <div className={styles.CurrentPlanBadge}>
                  {t('CurrentPlan')}
                </div>
              )} */}
              
              <div className={styles.TierHeader}>
                <div 
                  className={styles.TierIcon}
                  style={{ backgroundColor: getTierIconColor(tier.tierLevel || 0) + '20' }}
                >
                  {getTierIcon(tier.tierLevel || 0)}
                </div>
                <h4 className={styles.TierName}>{tier.name}</h4>
                <p className={styles.TierDescription}>{tier.description}</p>
              </div>

              {currentPrice && !isEnterprisePlan(tier) ? (
                <div className={styles.PricingSection}>
                  <div className={styles.Price}>
                    {isFreePlan(tier) ? (
                      t('FreePrice')
                    ) : (
                      <>
                        {getCurrencySymbol(currentPrice.currency || 'USD')}{Math.floor(currentPrice.unitAmountCents || 0) / 100}
                        <div className={styles.TinyCurrency}>
                          {(currentPrice.currency || 'USD').toUpperCase()}
                        </div>
                      </>
                    )}
                  </div>
                  {!isFreePlan(tier) ? (
                    <p className={styles.PricePeriod}>
                      {t('BilledInterval', { interval: interval === 'year' ? t('Annually') : t('Monthly') })}
                      <br />
                      {interval === 'year' && !isFreePlan(tier) && (
                        <span className={styles.PriceDiscount}>
                          -{calculateDiscountPercentage(tier)}% {t.withBaseKey('CommonWords')('Discount') || 'Discount'}
                        </span>
                      )}
                    </p>
                  ) : (
                    <div className={styles.EmptyPricePeriod} />
                  )}
                </div>
              ): (
              <div className={styles.EmptyPricingSection}>
                <div className={styles.Price}>
                  {t('CustomPrice')}
                </div>
              </div>
              )}

              {renderFeatures(tier)}

              {isCurrentPlan ? (
                <StyledButton className={`${styles.CtaButton}`} disabled title={t('CurrentPlan')} />
              ) : isSelected && selectedPrice ? (
                <StyledButton
                  noShadow
                  title={isEnterprisePlan(tier) ? t('TalkToSales') : 
                    tierChangeDirection === 'downgrade' ? `${t('DowngradeTo')} ${tier.name}` :
                    `${t('UpgradeTo')} ${tier.name}`}
                  className={`${styles.CtaButton}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isEnterprisePlan(tier)) {
                      handleTalkToSales();
                    } else {
                      handleContinue();
                    }
                  }}
                />
              ) : (
                <StyledButton 
                  noShadow
                  color={'light'}
                  className={`${styles.CtaButton}`}
                  onClick={() => handleTierChange(tier)}
                  title={`${t('SelectPlan')} ${tier.name}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PricingDisplay;


